'use client';

import { Checkbox } from '@ahafood/ui/components/checkbox';
import { FormControl, FormField, FormItem } from '@ahafood/ui/components/form';
import { Label } from '@ahafood/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@ahafood/ui/components/radio-group';
import { formatCurrency } from '@ahm/common-helpers';
import type { Control } from 'react-hook-form';
import { toast } from 'sonner';
import type { Customization } from '@/types/item';

interface CustomizationGroupProps {
  customization: Customization;
  control: Control<any>;
}

export function CustomizationGroup({
  customization,
  control,
}: CustomizationGroupProps) {
  const isMultiSelect = customization.min_permitted === 0;

  return (
    <div className="mb-4">
      <div className="flex flex-row items-center gap-2 px-4 py-2 text-sm font-semibold justify-between">
        <h2 className="text-neutral-90 flex-1">{customization.name}</h2>
        <span className="text-neutral-50">
          {customization.min_permitted > 0 ? (
            `Chọn ít nhất ${customization.min_permitted}`
          ) : (
            <span className="flex flex-col text-right">
              Không bắt buộc{' '}
              <span className="text-xs text-neutral-50 font-normal">
                (Chọn tối đa {customization.max_permitted})
              </span>
            </span>
          )}
        </span>
      </div>

      <FormField
        control={control}
        name={`customizations.${customization._id}`}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              {isMultiSelect ? (
                <MultiSelectOptions
                  customization={customization}
                  field={field}
                />
              ) : (
                <SingleSelectOptions
                  customization={customization}
                  field={field}
                />
              )}
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}

function MultiSelectOptions({
  customization,
  field,
}: {
  customization: Customization;
  field: any;
}) {
  const fieldValue = Array.isArray(field.value) ? field.value : [];

  const handleCheckedChange = (checked: boolean, optionId: string) => {
    let newValue = [...fieldValue];
    if (checked) {
      if (
        newValue.length < customization.max_permitted &&
        !newValue.includes(optionId)
      ) {
        newValue.push(optionId);
      } else if (
        newValue.length >= customization.max_permitted &&
        !newValue.includes(optionId)
      ) {
        if (customization.max_permitted === 1) {
          // Nếu chỉ được chọn 1, chuyển sang option mới luôn
          newValue = [optionId];
        } else {
          toast.error(
            `Bạn đã chọn tối đa ${customization.max_permitted} tùy chọn cho ${customization.name}.`
          );
          return;
        }
      }
    } else {
      newValue = newValue.filter((id) => id !== optionId);
    }
    field.onChange(newValue);
  };

  return (
    <div className="flex flex-col gap-1">
      {customization.options?.map((option) => (
        <Label
          htmlFor={`option-${customization._id}-${option._id}`}
          key={option._id}
          className="flex flex-row flex-nowrap items-start gap-2 p-4 w-full bg-white"
        >
          <Checkbox
            id={`option-${customization._id}-${option._id}`}
            checked={fieldValue.includes(option._id)}
            onCheckedChange={(checked) =>
              handleCheckedChange(Boolean(checked), String(option._id))
            }
          />
          <span className="text-neutral-70 flex-1">{option.name}</span>
          {option.price > 0 && (
            <span className="text-neutral-70 flex-none">
              +{formatCurrency(option.price)}
            </span>
          )}
        </Label>
      ))}
    </div>
  );
}

function SingleSelectOptions({
  customization,
  field,
}: {
  customization: Customization;
  field: any;
}) {
  const fieldValue = typeof field.value === 'string' ? field.value : '';

  return (
    <RadioGroup
      className="gap-px"
      value={fieldValue}
      onValueChange={field.onChange}
    >
      {customization.options?.map((option) => (
        <Label
          htmlFor={`option-${customization._id}-${option._id}`}
          key={option._id}
          className="flex flex-row flex-nowrap items-start gap-2 p-4 w-full bg-white"
        >
          <RadioGroupItem
            className="text-sm font-semibold"
            value={String(option._id)}
            id={`option-${customization._id}-${option._id}`}
          />
          <span className="text-neutral-70 flex-1">{option.name}</span>
          {option.price > 0 && (
            <span className="text-neutral-70 flex-none">
              +{formatCurrency(option.price)}
            </span>
          )}
        </Label>
      ))}
    </RadioGroup>
  );
}
